// "Open this account in Google Ads" — the Google analogue of the Meta rows'
// Ads-Manager deep link. PURE + tested.
//
// ⚠️ THE URL FORMAT NEEDS LIVE CONFIRMATION. Meta's deep link is clean (`act=`);
// Google's is not documented as a stable contract, and `__c` is the parameter the
// Google Ads UI itself carries when an account is selected. It is therefore the
// DEFAULT, not a certainty — if a first click lands on the account picker instead
// of the account, `GOOGLE_ADS_ACCOUNT_URL` is the single line to change and
// nothing else moves.
//
// ⚠️ A CURATED LINK WINS OVER A CONSTRUCTED ONE, but only when it is unambiguous
// AND absolute — see `buildCustomerUrlMap` and `googleAdsAccountHref`.

import { normalizeCustomerId } from './googleAdsAttribution.js';
import { isAbsoluteHttpUrl } from './absoluteHttpUrl.js';

/** Where a constructed link points. Digits only — Google's dashed display id 404s. */
export function googleAdsCampaignsUrl(customerId: string): string {
  return `https://ads.google.com/aw/campaigns?__c=${customerId}`;
}

/**
 * Accept a stored `Account.googleAdsUrl` only when it is an ABSOLUTE http(s) URL.
 *
 * A curated value is free text, and a bare "ads.google.com/aw/campaigns" used as
 * an href is a RELATIVE path — it would navigate inside kyck rather than out to
 * Google, which reads as the app breaking rather than as a bad setting. Anything
 * we can't vouch for falls back to the constructed link, which is derived from
 * the customer id itself and therefore cannot point at the wrong account.
 */
export function isUsableAccountUrl(url: string | null | undefined): boolean {
  // The check itself is shared with the account link icons — see absoluteHttpUrl.ts.
  return isAbsoluteHttpUrl(url);
}

/** A Google Ads account as the tree row carries it. */
export interface GoogleAdsCustomerRef {
  id: string;
  name?: string | null;
  /** The curated `Account.googleAdsUrl`, when the roster has an unambiguous one. */
  url?: string | null;
}

/**
 * The href for a Google Ads account, or null when there is nothing safe to link.
 *
 * Curated first (a human put it there, and it may point somewhere more useful
 * than the campaigns list), constructed second. Null when the id carries no
 * digits at all — a link that cannot name its target is worse than plain text.
 */
export function googleAdsAccountHref(customer: GoogleAdsCustomerRef | undefined): string | null {
  if (!customer) return null;
  if (isUsableAccountUrl(customer.url)) return customer.url!.trim();
  const digits = normalizeCustomerId(customer.id);
  return digits ? googleAdsCampaignsUrl(digits) : null;
}

/**
 * The href for a tree row's Google-account cell.
 *
 * ⚠️ ONLY when the row resolves to EXACTLY ONE customer. A Location whose
 * campaigns span several Google accounts renders "N Google accounts", and there
 * is no single target to send the click to — picking the first would open the
 * wrong account roughly half the time, silently.
 */
export function googleAccountHrefForRow(
  customers: readonly GoogleAdsCustomerRef[],
): string | null {
  return customers.length === 1 ? googleAdsAccountHref(customers[0]) : null;
}

/** The roster shape this needs — a lean subset of Account. */
export interface CustomerUrlSource {
  googleAdsCustomerId: string | null;
  googleAdsUrl: string | null;
}

/**
 * Map normalized customer id → the curated link, for the ids where the roster
 * AGREES on one.
 *
 * ⚠️ Several kyck locations can share one Google customer id (§3b), and each
 * carries its OWN `googleAdsUrl`. Where two holders disagree we deliberately
 * store NOTHING and let the constructed link take over: the constructed link is
 * derived from the customer id, so it is guaranteed to mean the right account,
 * whereas picking one of two conflicting curated values is a coin flip that
 * looks authoritative. Holders that simply left the field blank are ignored —
 * blank is not a disagreement.
 */
export function buildCustomerUrlMap(
  accounts: readonly CustomerUrlSource[],
): Map<string, string> {
  const seen = new Map<string, string | null>(); // null = conflicting
  for (const a of accounts) {
    const key = normalizeCustomerId(a.googleAdsCustomerId);
    if (!key) continue;
    const url = (a.googleAdsUrl ?? '').trim();
    if (!isUsableAccountUrl(url)) continue;
    if (!seen.has(key)) seen.set(key, url);
    else if (seen.get(key) !== url) seen.set(key, null);
  }
  const out = new Map<string, string>();
  for (const [key, url] of seen) if (url !== null) out.set(key, url);
  return out;
}
